import type { ReactElement } from "react";
import type { DashboardOrderFateCardProps } from "@/modules/dashboard/types/dashboard-analytics.types";

/** Below this share a segment cannot hold its percentage without clipping. */
const MIN_LABELLED_SHARE = 0.14;

/**
 * At or above this share a segment holds its ORDER COUNT at EVERY width,
 * including 375px where the bar is ~309px — 0.24 buys ~74px there.
 *
 * Sized against the widest count a real store produces, not the demo data:
 * measured at 9.5px, "١٬٠٠٠ طلب" needs ~61px and "١٢٬٣٤٥ طلب" ~64px, both plus
 * 12px inline padding. The previous 0.2 bought only ~62px and was justified
 * against "١٢ طلب" (~40px) — fine for a two-digit count, but any store with
 * four-figure orders in a ~20% segment got its count truncated to an ellipsis.
 *
 * Between MIN_LABELLED_SHARE and this, the count shows from `sm` up only: the
 * card is full-bleed, so share maps to pixels linearly with the viewport, and
 * 14% of a ≥576px card is ~80px — enough — while 14% of 309px is ~43px, which
 * is not. A share threshold alone would either clip on mobile or drop the count
 * on a 1600px screen that had room for it.
 *
 * Ceiling: a share-based gate cannot know the string's actual width, so a
 * six-figure count in a barely-qualifying segment could still clip. Measuring
 * the rendered text would be exact; it costs a layout pass per segment and is
 * only worth it if that case shows up. Nothing is ever lost either way — the
 * count is printed in the list below the bar at every width.
 */
const MIN_COUNT_LABELLED_SHARE = 0.24;

/**
 * "Where did the money go?" — ONE continuous composition bar over the range,
 * split by outcome (collected · pending · lost), with the amounts and order
 * counts spelled out beneath it and a one-line reading.
 *
 * The bar is deliberately gapless: rounding lives on the outer container and
 * `overflow-hidden` clips the fills, so the three colours read as a single bar
 * whose parts sum to the whole rather than as three separate chips. Every
 * amount, share and order count is also printed as text below, so nothing is
 * encoded in colour alone and nothing is gated behind hover — which does not
 * exist on touch. The `title` breakdown is an enhancement on top of that.
 */
export function DashboardOrderFateCard({
  title,
  subtitle,
  segments,
  takeaway,
  emptyTitle,
  emptyBody,
  formatValue,
  barSummary,
}: DashboardOrderFateCardProps): ReactElement {
  const total = segments.reduce((sum, segment) => sum + segment.amount, 0);
  const isEmpty = total <= 0;

  return (
    <section className="overflow-hidden rounded-[18px] border border-[color:var(--line2)] bg-[color:var(--card)] p-4 sm:p-[18px]">
      <div className="min-w-0">
        <h2 className="text-[15px] font-bold text-[color:var(--text-primary)]">
          {title}
        </h2>
        <p className="mt-0.5 text-[12.5px] text-[color:var(--text-muted)]">
          {subtitle}
        </p>
      </div>

      {isEmpty ? (
        <div className="mt-4 rounded-[12px] border border-dashed border-[color:var(--border-light)] bg-[color:var(--soft)] px-4 py-6 text-center">
          <p className="text-[13px] font-semibold text-[color:var(--text-primary)]">
            {emptyTitle}
          </p>
          <p className="mt-1 text-[12px] leading-[1.5] text-[color:var(--text-muted)]">
            {emptyBody}
          </p>
        </div>
      ) : (
        <>
          {/* ONE bar: no gap between fills, radius + clipping on the container
              only. `flex-row` inside the RTL page already fills from the inline
              start, so no manual reversing is needed. */}
          {/* 44px tall rather than 36: the segments now carry two stacked
              lines, and 44 is also the minimum comfortable long-press target
              on touch, where these fills are the only way to reach the title. */}
          <div className="mt-4 flex h-11 w-full overflow-hidden rounded-[8px]">
            {segments.map((segment) => {
              const share = segment.amount / total;
              if (share <= 0) return null;
              const summary = barSummary(
                segment.label,
                formatValue(segment.amount),
                segment.ordersLabel,
              );
              return (
                <div
                  key={segment.id}
                  role="img"
                  // One string, both channels: `title` for the mouse,
                  // `aria-label` so the readout is not mouse-only. Neither is
                  // load-bearing — the full breakdown is text below.
                  aria-label={summary}
                  title={summary}
                  className="flex min-w-0 flex-col items-center justify-center"
                  style={{
                    flexGrow: share,
                    flexBasis: 0,
                    background: segment.color,
                  }}
                >
                  {/* Only labelled when the segment is demonstrably wide enough
                      to hold the text without clipping. `ink` is required, not
                      optional: which foreground clears 4.5:1 depends on the
                      fill AND the theme, so each segment names a token that
                      flips with the theme rather than defaulting to white. The
                      same percentage is repeated in text below, so the value is
                      never gated behind this label. */}
                  {share >= MIN_LABELLED_SHARE ? (
                    <span
                      className={[
                        "max-w-full truncate px-1.5 text-[11px] font-bold tabular-nums",
                      ].join(" ")}
                      style={{
                        color: segment.ink,
                      }}
                    >
                      <bdi>{Math.round(share * 100)}%</bdi>
                    </span>
                  ) : null}
                  {/* The order count, ON the fill — a share alone doesn't say
                      whether 48% of the money is one order or ten. Dropped
                      (never clipped) on segments too narrow to hold it; the
                      count is still in the list below either way. */}
                  {share >= MIN_LABELLED_SHARE ? (
                    <span
                      className={[
                        "mt-[3px] max-w-full truncate px-1.5 text-[9.5px] font-semibold leading-none",
                        // Wide enough at any width, or only once the card is.
                        share >= MIN_COUNT_LABELLED_SHARE
                          ? "block"
                          : "hidden sm:block",
                      ].join(" ")}
                      style={{
                        color: segment.ink,
                        opacity: 0.9,
                      }}
                    >
                      <bdi>{segment.ordersLabel}</bdi>
                    </span>
                  ) : null}
                </div>
              );
            })}
          </div>

          {/* Amounts, shares AND order counts in text — never colour-only, and
              never hover-only. */}
          <dl className="mt-3 grid grid-cols-1 gap-x-4 gap-y-2.5 sm:grid-cols-3">
            {segments.map((segment) => (
              // Mobile puts the label and the figures on ONE line (label at the
              // inline start, figures at the inline end) so three segments cost
              // three rows rather than nine; from `sm` the grid gives each its
              // own column and the figures sit under the label.
              <div
                key={segment.id}
                className="flex min-w-0 items-start justify-between gap-3 sm:block"
              >
                <dt className="inline-flex min-w-0 items-center gap-2 text-[12px] text-[color:var(--text-muted)]">
                  <span
                    aria-hidden="true"
                    className="h-[9px] w-[9px] shrink-0 rounded-[3px]"
                    style={{ background: segment.color }}
                  />
                  <span className="truncate">{segment.label}</span>
                </dt>
                {/* Amount, share AND order count, in text tokens. This is the
                    relief channel for the in-fill percentage above: on-fill ink
                    relies on a text shadow rather than a token contrast ratio,
                    so the same numbers are always readable here at full
                    contrast — and none of them needs hover. */}
                <dd className="min-w-0 text-end sm:mt-0.5 sm:text-start">
                  <span className="flex flex-wrap items-baseline justify-end gap-x-1.5 sm:justify-start">
                    <span className="font-mono text-[13px] font-bold tabular-nums text-[color:var(--text-primary)]">
                      <bdi>{formatValue(segment.amount)}</bdi>
                    </span>
                    <span className="font-mono text-[11px] tabular-nums text-[color:var(--text-muted)]">
                      <bdi>{Math.round((segment.amount / total) * 100)}%</bdi>
                    </span>
                  </span>
                  <span className="mt-0.5 block truncate text-[11px] text-[color:var(--text-muted)]">
                    <bdi>{segment.ordersLabel}</bdi>
                  </span>
                </dd>
              </div>
            ))}
          </dl>

          {/* Takeaway strip — full-bleed against the card padding, matching the
              approved concept's `.note` band. */}
          <p className="-mx-4 -mb-4 mt-4 border-t border-[color:var(--hair)] bg-[color:var(--soft)] px-4 py-3 text-[11.5px] leading-[1.5] text-[color:var(--text-secondary)] sm:-mx-[18px] sm:-mb-[18px] sm:px-[18px]">
            {takeaway}
          </p>
        </>
      )}
    </section>
  );
}
